<template>
  <a-modal
    :title="$t('all.edit') + ' ' + $t('accueil.activite')"
    :visible="visible"
    :width="800"
    :confirm-loading="loading"
    @ok="handleOk"
    @cancel="handleCancel"
    :ok-text="$t('action.enregistrer')"
    :cancel-text="$t('action.annuler')"
  >
    <div v-if="activityData">
      <div class="mb-3">
        <h5>{{ activityData.name }}</h5>
        <a-tag :color="activityData.mode === 'M' ? 'blue' : 'green'">
          {{ activityData.mode === 'M' ? 'Paiement mensuel' : 'Paiement par tranche' }}
        </a-tag>
      </div>

      <!-- Daily Calendar Component for Daily Activities -->
      <div v-if="activityData.daily" class="calendar-container">
        <DailyCalendar
          :selected-dates="selectedDates"
          :activity-start="activityStart"
          :activity-end="activityEnd"
          :payment-data="selectedDays"
          :show-selected-dates="false"
          @add-date="addSelectedDate"
          @remove-date="removeSelectedDate"
          @clear-all-dates="clearAllDates"
        />
      </div>

      <!-- Month Selection for Non-Daily Activities (Mode M) -->
      <div v-else-if="!activityData.daily && activityData.mode === 'M'" class="month-selection-container">
        <h6>{{ $t('inscription.selectionnerMois') }}:</h6>
        <div class="month-grid">
          <div 
            v-for="month in availableMonths" 
            :key="month.value"
            class="month-item"
            :class="{ 
              'selected': isMonthSelected(month.value),
              'paid': getMonthPaymentStatus(month.value) === 'paid',
              'partially-paid': getMonthPaymentStatus(month.value) === 'partially-paid'
            }"
            @click="toggleMonthSelection(month.value)"
          >
            <div class="month-label">{{ month.label }}</div>
            <div class="month-status">
              <a-tag 
                :color="getMonthTagColor(month.value)" 
                size="small"
              >
                {{ getMonthStatusLabel(month.value) }}
              </a-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- Tranche Selection for Non-Daily Activities (Mode T) -->
      <div v-else-if="!activityData.daily && activityData.mode === 'T'" class="tranche-selection-container">
        <h6>{{ $t('inscription.selectionnerTranches') }}:</h6>
        <div class="tranche-grid">
          <div 
            v-for="tranche in availableTranches" 
            :key="tranche.trancheNumber"
            class="tranche-item"
            :class="{ 
              'selected': isTrancheSelected(tranche.trancheNumber),
              'paid': getTranchePaymentStatus(tranche.trancheNumber) === 'paid',
              'partially-paid': getTranchePaymentStatus(tranche.trancheNumber) === 'partially-paid'
            }"
            @click="toggleTrancheSelection(tranche.trancheNumber)"
          >
            <div class="tranche-label">{{ $t('inscription.tranche') }} {{ tranche.trancheNumber }}</div>
            <div class="tranche-status">
              <a-tag 
                :color="getTrancheTagColor(tranche.trancheNumber)" 
                size="small"
              >
                {{ getTrancheStatusLabel(tranche.trancheNumber) }}
              </a-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Days/Months/Tranches Summary -->
      <div class="mt-3">
        <h6>{{ getSummaryTitle() }}:</h6>
        <div class="selected-days">
            
          <a-tag
            v-for="day in selectedDays"
            :key="day.date"
            :color="getTagColor(day)"
            :closable="!day.paid && !day.remaining"
            @close="removeSelectedDate(day.date)"
            class="mb-2 mr-2"
          >
            {{ formatFeeName(day.date, activityData.daily) }} - {{ getTagLabel(day) }}
          </a-tag>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import moment from 'moment';
import DailyCalendar from "@/components/comptabilite/DailyCalendar.vue";
import { formatFeeName } from "@/utilities/settings";
import apiClient from "@/services/axios";

export default {
  name: 'EditActivityModal',
  components: {
    DailyCalendar
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    activityData: {
      type: Object,
      default: null
    },
    studentId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: false,
      selectedDates: [],
      selectedDays: [],
      originalDays: [],
      activityStart: null,
      activityEnd: null,
    };
  },
  computed: {
    availableMonths() {
      if (!this.activityData || this.activityData.daily || this.activityData.mode !== 'M') return [];
      
      const months = [];
      const start = moment(this.activityData.start);
      const end = moment(this.activityData.end);
      
      let current = start.clone().startOf('month');
      while (current.isSameOrBefore(end, 'month')) {
        months.push({
          value: current.format('YYYY-MM-01'),
          label: current.format('MMMM YYYY')
        });
        current.add(1, 'month');
      }
      
      return months;
    },
    availableTranches() {
      if (!this.activityData || this.activityData.daily || this.activityData.mode !== 'T') return [];
      return this.activityData.tranches || [];
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.activityData) {
        this.initializeData();
      }
    },
    activityData: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.initializeData();
        }
      },
      deep: true
    }
  },
  methods: {
    formatFeeName,
    initializeData() {
      if (!this.activityData || !this.activityData.fees) return;

      this.selectedDays = this.activityData.fees
        .filter(fee => fee.feeName !== 'inscription')
        .map(fee => ({
          date: fee.feeName,
          paid: fee.isPayed.fullyPaid,
          remaining: fee.isPayed.remaining && fee.isPayed.remaining>0,
          feeName: fee.feeLabel,
        }));

      this.selectedDates = [...new Set(this.selectedDays.map(day => day.date))];
      
      this.activityStart = this.activityData.start ? moment(this.activityData.start) :null;
      this.activityEnd = this.activityData.end ? moment(this.activityData.end) : null;
      
      this.originalDays = [...this.selectedDays];
    },

    addSelectedDate(dateStr) {
      const existingDay = this.selectedDays.find(day => day.date === dateStr);
      
      if (!existingDay) {
        if (!this.selectedDates.includes(dateStr)) {
          this.selectedDates.push(dateStr);
        }
        
        const newDay = {
          date: dateStr,
          paid: false,
          remaining: null,
          feeName: `${moment(dateStr).format('MMMM')} (${dateStr})`,
        };
        this.selectedDays.push(newDay);
        
        this.cleanupDuplicates();
      }
    },

    removeSelectedDate(dateStr) {
      const dateIndex = this.selectedDates.indexOf(dateStr);
      if (dateIndex > -1) {
        this.selectedDates.splice(dateIndex, 1);
      }
      
      const dayIndex = this.selectedDays.findIndex(d => d.date === dateStr);
      if (dayIndex > -1) {
        this.selectedDays.splice(dayIndex, 1);
      }
    },

    clearAllDates() {
      this.selectedDates = [];
      this.selectedDays = [];
    },

    cleanupDuplicates() {
      this.selectedDates = [...new Set(this.selectedDates)];
      
      const uniqueDays = this.selectedDays.filter((day, index, self) => 
        index === self.findIndex(d => d.date === day.date)
      );
      this.selectedDays = uniqueDays;
    },


    getTagColor(day) {
      if (day.paid) {
        return 'green';
      } else if (day.remaining) {
        return 'orange';
      } else {
        return 'red';
      }
    },

    getTagLabel(day) {
      if (day.paid) {
        return this.$t('paiement.paye');
      } else if (day.remaining) {
        return this.$t('profil.paidWithAvance');
      } else {
        return this.$t('paiement.impaye');
      }
    },

    // Month selection methods for non-daily activities
    isMonthSelected(monthValue) {
      return this.selectedDays.some(day => day.date === monthValue);
    },

    getMonthPaymentStatus(monthValue) {
      const day = this.selectedDays.find(d => d.date === monthValue);
      if (!day) return 'unpaid';
      
      if (day.paid) return 'paid';
      if (day.remaining) return 'partially-paid';
      return 'unpaid';
    },

    getMonthTagColor(monthValue) {
      const status = this.getMonthPaymentStatus(monthValue);
      if (status === 'paid') return 'green';
      if (status === 'partially-paid') return 'orange';
      return 'red';
    },

    getMonthStatusLabel(monthValue) {
      const status = this.getMonthPaymentStatus(monthValue);
      if (status === 'paid') return this.$t('paiement.paye');
      if (status === 'partially-paid') return this.$t('profil.paidWithAvance');
      return this.$t('paiement.impaye');
    },

    toggleMonthSelection(monthValue) {
      const existingDay = this.selectedDays.find(day => day.date === monthValue);
      
      if (existingDay) {
        // Check if it's paid or partially paid - don't allow removal
        if (existingDay.paid || existingDay.remaining) {
          return; // Can't remove paid/partially paid months
        }
        // Remove unpaid month
        this.removeSelectedDate(monthValue);
      } else {
        // Add new month
        this.addSelectedMonth(monthValue);
      }
    },

    addSelectedMonth(monthValue) {
      const newDay = {
        date: monthValue,
        paid: false,
        remaining: false,
        feeName: moment(monthValue).format('MMMM YYYY'),
      };
      
      this.selectedDays.push(newDay);
      this.selectedDates.push(monthValue);
      this.cleanupDuplicates();
    },

    isTrancheSelected(trancheNumber) {
      return this.selectedDays.some(day => day.date === trancheNumber.toString());
    },

    getTranchePaymentStatus(trancheNumber) {
      const day = this.selectedDays.find(d => d.date === trancheNumber.toString());
      if (!day) return 'unpaid';
      
      if (day.paid) return 'paid';
      if (day.remaining) return 'partially-paid';
      return 'unpaid';
    },

    getTrancheTagColor(trancheNumber) {
      const status = this.getTranchePaymentStatus(trancheNumber);
      if (status === 'paid') return 'green';
      if (status === 'partially-paid') return 'orange';
      return 'red';
    },

    getTrancheStatusLabel(trancheNumber) {
      const status = this.getTranchePaymentStatus(trancheNumber);
      if (status === 'paid') return this.$t('paiement.paye');
      if (status === 'partially-paid') return this.$t('profil.paidWithAvance');
      return this.$t('paiement.impaye');
    },

    toggleTrancheSelection(trancheNumber) {
      const existingDay = this.selectedDays.find(day => day.date === trancheNumber.toString());
      
      if (existingDay) {
        // Check if it's paid or partially paid - don't allow removal
        if (existingDay.paid || existingDay.remaining) {
          return; // Can't remove paid/partially paid tranches
        }
        // Remove unpaid tranche
        this.removeSelectedDate(trancheNumber.toString());
      } else {
        // Add new tranche
        this.addSelectedTranche(trancheNumber);
      }
    },

    addSelectedTranche(trancheNumber) {
      const tranche = this.availableTranches.find(t => t.trancheNumber === trancheNumber);
      if (!tranche) return;

      const newDay = {
        date: trancheNumber.toString(),
        paid: false,
        remaining: false,
        feeName: `${this.$t('inscription.tranche')} ${trancheNumber}`,
      };
      
      this.selectedDays.push(newDay);
      this.selectedDates.push(trancheNumber.toString());
      this.cleanupDuplicates();
    },

    formatMonth(monthValue) {
      return moment(monthValue).format('MMM YYYY');
    },

    getSummaryTitle() {
      if (this.activityData.daily) {
        return this.$t('inscription.datesSelectionnees');
      } else if (this.activityData.mode === 'M') {
        return this.$t('inscription.moisSelectionnes');
      } else if (this.activityData.mode === 'T') {
        return this.$t('inscription.tranchesSelectionnees');
      }
      return this.$t('inscription.elementsSelectionnes');
    },

    async handleOk() {
      this.loading = true;
      
      try {
        const unpaidFees = this.selectedDays
          .filter(day => !day.paid && !day.remaining)
          .map(day => day.date);

        await apiClient.patch(`/payment/fees/${this.activityData._id}`, {
          fees: unpaidFees
        });
        
        this.$message.success(this.$t('success.modification'));
        await this.$emit('updatePayment');
        this.handleCancel();
      } catch (error) {
        this.$message.error(this.$t('error.modif'));
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('close');
      this.resetData();
    },

    resetData() {
      this.selectedDays = [];
      this.selectedDates = [];
      this.originalDays = [];
      this.activityStart = null;
      this.activityEnd = null;
    }
  }
};
</script>

<style scoped>
.calendar-container {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
}

.selected-days {
  max-height: 120px;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px;
  background: #f9f9f9;
}

/* Month selection styles */
.month-selection-container {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
  margin-bottom: 16px;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.month-item {
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  text-align: center;
}

.month-item:hover:not(.paid):not(.partially-paid) {
  border-color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.month-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.month-item.paid {
  border-color: #52c41a;
  background-color: #f6ffed;
  cursor: not-allowed;
  opacity: 0.8;
}

.month-item.partially-paid {
  border-color: #faad14;
  background-color: #fffbe6;
  cursor: not-allowed;
  opacity: 0.8;
}

.month-label {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  margin-bottom: 8px;
}

.month-status {
  display: flex;
  justify-content: center;
}

/* Tranche selection styles */
.tranche-selection-container {
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
  margin-bottom: 16px;
}

.tranche-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.tranche-item {
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  text-align: center;
}

.tranche-item:hover:not(.paid):not(.partially-paid) {
  border-color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.tranche-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.tranche-item.paid {
  border-color: #52c41a;
  background-color: #f6ffed;
  cursor: not-allowed;
  opacity: 0.8;
}

.tranche-item.partially-paid {
  border-color: #faad14;
  background-color: #fffbe6;
  cursor: not-allowed;
  opacity: 0.8;
}

.tranche-label {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  margin-bottom: 8px;
}

.tranche-status {
  display: flex;
  justify-content: center;
}
</style>
